/**
 * MemberWaiting - what a non-owner of an unfinished company sees instead of onboarding.
 *
 * v4.29 - 2026-10-13 - ONBOARDING-NOT-ENFORCED-01 (A.2 in the dispatch): a Member whose company
 *                      has not finished setup used to be dropped into the owner's onboarding steps
 *                      (`/onboarding` ran basic-info and discovery for them; the three writers
 *                      have no role test — `d75_agent_service.py:3098, 5626, 6140`). This page
 *                      stops that: when `firstScreenFor` returns `/onboarding` and the role is
 *                      not `owner`, App.tsx renders this view instead. All text is translated;
 *                      no customer-facing literal lives in this file.
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Paper, Typography, Button } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useAuth } from '@clerk/clerk-react';
import { logClientDecision } from '../services/portalRequest';
import { getMeSnapshot } from '../hooks/usePermissions';

const MemberWaiting: React.FC = () => {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  useEffect(() => {
    // The log line carries the active company id and the viewer's role; it never carries an email.
    const snap = getMeSnapshot();
    logClientDecision('member_waiting_shown', {
      company: snap?.activeTenantUuid || 'none',
      role: snap?.role || 'unknown',
    });
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      window.location.assign('/login');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', py: 6 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }} elevation={3}>
          <HourglassEmptyIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            {t('teamAccess.memberWaiting.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('teamAccess.memberWaiting.body')}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleSignOut}
            sx={{ textTransform: 'none' }}
          >
            {t('teamAccess.memberWaiting.signOut')}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default MemberWaiting;
